import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Chip,
    FormControl,
    FormControlLabel,
    RadioGroup,
    Radio,
    Divider
} from '@mui/material';
import {
    Public as GlobalIcon,
    Home as CustomIcon,
} from '@mui/icons-material';
import { useCreatePresetRequestMutation } from '../../../../store/services/delivery-service';
import MainCard from "../../../../components/cards/MainCard";
import { getMonthDifference } from '../../utils/dateUtils';

const MODAL_BORDER_RADIUS = '8px';

/**
 * Modal component for exporting calendar items to a preset template
 */
const ExportToPresetModal = ({
    open,
    onClose,
    calendarItems,
    showNotification
}) => {
    const [presetName, setPresetName] = useState('');
    const [presetItemsWithPattern, setPresetItemsWithPattern] = useState([]);
    const [exportAttempted, setExportAttempted] = useState(false);
    // Set initial state for preset type to 'global' for admins
    const [presetType, setPresetType] = useState('global');

    // Get tenancy info and user role from Redux store
    const { tenancyId } = useSelector((state) => state.tenancy);
    const { role } = useSelector((state) => state.auth.user || {});

    // Check if user is super admin
    const isSuperAdmin = role === "Administrators";

    const [createPresetRequest, { isLoading: isCreatingPreset }] = useCreatePresetRequestMutation();

    // Reset form state when modal opens
    useEffect(() => {
        if (open) {
            setPresetName('');
            // Set default preset type based on user role
            setPresetType(isSuperAdmin ? 'global' : 'custom');
            setExportAttempted(false);
        }
    }, [open, isSuperAdmin]);

    // Calculate month-day patterns when the modal opens with calendar items
    useEffect(() => {
        if (open && calendarItems.length > 0) {
            const sortedItems = [...calendarItems].sort((a, b) =>
                new Date(a.scheduledFor) - new Date(b.scheduledFor)
            );

            // Find the earliest date
            const earliestDate = new Date(sortedItems[0].scheduledFor);
            earliestDate.setHours(0, 0, 0, 0); // Normalize time

            // Calculate relative month and day of month for each item
            const itemsWithMonthDay = sortedItems.map(item => {
                const itemDate = new Date(item.scheduledFor);
                itemDate.setHours(0, 0, 0, 0); // Normalize time

                // Calculate relative month (1-based)
                const monthDiff = getMonthDifference(earliestDate, itemDate);
                const relativeMonth = monthDiff + 1; // Make it 1-based

                // Get day of month
                const dayOfMonth = itemDate.getDate();

                return {
                    ...item,
                    relativeMonth,
                    dayOfMonth,
                    adjustForMonthEnd: true,
                    adjustForWeekends: false
                };
            });

            setPresetItemsWithPattern(itemsWithMonthDay);
        }
    }, [open, calendarItems]);

    const handleCreatePreset = async () => {
        setExportAttempted(true);

        if (!presetName.trim()) {
            return;
        }

        try {
            const user = JSON.parse(localStorage.getItem('user'));
            const userId = user?.email || '';

            // Ensure non-admins can only create custom presets
            const effectivePresetType = isSuperAdmin ? presetType : 'custom';

            const presetData = {
                name: presetName,
                createdById: userId,
                // Set tenancyId based on effective preset type
                tenancyId: effectivePresetType === 'global' ? 0 : tenancyId,
                presetItems: presetItemsWithPattern.map(item => ({
                    contentId: item.contentId,
                    contentType: item.contentType,
                    relativeMonth: item.relativeMonth,
                    dayOfMonth: item.dayOfMonth,
                    adjustForMonthEnd: true,
                    adjustForWeekends: false
                }))
            };

            // Log the request data for debugging
            console.log('Creating preset with data:', {
                presetType: effectivePresetType,
                tenancyId: presetData.tenancyId,
                isSuperAdmin
            });

            await createPresetRequest(presetData).unwrap();
            showNotification(`${effectivePresetType === 'global' ? 'Global' : 'Custom'} template created successfully`, 'success');
            onClose();
        } catch (error) {
            console.error('Error creating template:', error);
            showNotification(`Error creating template: ${error.data?.error || 'Unknown error'}`, 'error');
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                style: { borderRadius: MODAL_BORDER_RADIUS },
                sx: {
                    bgcolor: 'background.default'
                }
            }}
        >
            <DialogTitle>
                <Typography variant="h4" >
                    Save as Journey Template
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 1, mb: 3 }}>
                    <TextField
                        label="Template Name"
                        value={presetName}
                        onChange={(e) => setPresetName(e.target.value)}
                        fullWidth
                        required
                        error={exportAttempted && !presetName.trim()}
                        helperText={exportAttempted && !presetName.trim() ? "Name is required" : ""}
                    />
                </Box>

                {/* Add template type selection (only for admins) */}
                {isSuperAdmin && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Template Type
                        </Typography>
                        <FormControl component="fieldset">
                            <RadioGroup
                                row
                                name="preset-type"
                                value={presetType}
                                onChange={(e) => setPresetType(e.target.value)}
                            >
                                <FormControlLabel
                                    value="custom"
                                    control={<Radio />}
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <CustomIcon sx={{ mr: 1 }} />
                                            <span>Custom Template</span>
                                        </Box>
                                    }
                                />
                                <FormControlLabel
                                    value="global"
                                    control={<Radio />}
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <GlobalIcon sx={{ mr: 1 }} />
                                            <span>Global Template</span>
                                        </Box>
                                    }
                                />
                            </RadioGroup>
                        </FormControl>
                        <Typography variant="body2" color="text.secondary">
                            {presetType === 'global'
                                ? "Global templates are available to all tenancies in the system."
                                : "Custom templates are only available to the currently selected tenancy."}
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                    </Box>
                )}

                <Typography variant="h6" gutterBottom>
                    Content Items ({presetItemsWithPattern.length})
                </Typography>

                <MainCard
                    content={true}
                    boxShadow={true}
                    border={false}
                    sx={{ bgcolor: 'background.default' }}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Month</TableCell>
                                <TableCell>Day</TableCell>
                                <TableCell>Content Type</TableCell>
                                <TableCell>Content Name</TableCell>
                                <TableCell>Original Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {presetItemsWithPattern.map((item, index) => (
                                <TableRow key={index}>
                                    <TableCell>
                                        <Chip
                                            label={`Month ${item.relativeMonth}`}
                                            color={"default"}
                                            variant={"outlined"}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={`Day ${item.dayOfMonth}`}
                                            color="secondary"
                                            variant="outlined"
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={item.contentType}
                                            color={item.contentType === 'Simulation' ? 'primary' : 'warning'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{item.contentId}</TableCell>
                                    <TableCell>{new Date(item.scheduledFor).toLocaleDateString()}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </MainCard>

                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2">
                        This template follows a month/day pattern, with Month 1 representing the first month when the template is applied.
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onClose}
                    variant="contained"
                    color="secondary"
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleCreatePreset}
                    variant="contained"
                    color="primary"
                    disabled={isCreatingPreset}
                >
                    {isCreatingPreset ? (
                        <CircularProgress size={24} />
                    ) : (
                        `Save as ${isSuperAdmin ? (presetType === 'global' ? 'Global' : 'Custom') : 'Custom'} Template`
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

ExportToPresetModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    calendarItems: PropTypes.array.isRequired,
    showNotification: PropTypes.func.isRequired
};

export default ExportToPresetModal;